import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Chart from "chart.js/auto";
import {
  Receipt,
  DollarSign,
  Smile,
  Wrench,
  Activity,
  Boxes,
  Truck,
  PackageSearch,
  Map as MapIcon,
  ShieldCheck,
  LayoutDashboard,
  FolderOpen,
  Clock,
  CheckCircle2,
  CalendarCheck,
  AlertTriangle,
  BarChart3,
  ClipboardPlus,
  Search,
  ShoppingCart,
  Printer,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { api, ApiClientError } from "../lib/api";
import { DataTable, type Column } from "../components/DataTable";
import { StatusBadge } from "../components/StatusBadge";

interface DashboardMetrics {
  totalOrders: number;
  totalSales: number;
  repairsCompleted: number;
  repairsOpen: number;
  repairsInProgress: number;
  fillRatePct: number;
  inventoryValue: number;
  partsInStock: number;
  partsLowStock: number;
  partsOutOfStock: number;
  totalCustomers: number;
  vipCustomers: number;
  cartonsNotStarted: number;
  cartonsInProgress: number;
  cartonsReceived: number;
  ordersReadyToShip: number;
  ordersInPicking: number;
  ordersShipped: number;
  technicianWorkload: { technician: string; count: number }[];
  ordersByType: { orderType: string; total: number }[];
}
interface TechnicianMetrics {
  openOrders: number;
  inProgress: number;
  completedToday: number;
  dueToday: number;
  overdue: number;
}
interface WorkQueueRow {
  id: string;
  customerName: string;
  equipment: string;
  status: string;
  promisedDate: string;
}

const money = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** A small "not real data" tag, shown next to any figure this build has no honest data source
 *  for (BR-011 / NFR-DATA-001). */
function Illustrative() {
  return <span className="text-[10px] font-bold uppercase tracking-wide text-ink-muted/70 ml-1.5 align-middle">Illustrative</span>;
}

function gaugeNeedlePoint(frac: number) {
  const clamped = Math.max(0, Math.min(1, frac));
  const angle = Math.PI * (1 - clamped);
  const r = 68;
  return { x: (100 + r * Math.cos(angle)).toFixed(1), y: (100 - r * Math.sin(angle)).toFixed(1) };
}

function Orderometer({ totalOrders }: { totalOrders: number }) {
  const needle = gaugeNeedlePoint(totalOrders / 2000);
  return (
    <div className="card p-4 flex flex-col">
      <div className="text-[11.5px] font-extrabold tracking-wider uppercase text-ink-muted">Orderometer</div>
      <div className="relative max-w-[236px] mx-auto mt-0.5 w-full">
        <svg viewBox="0 0 200 116" className="w-full block">
          <defs>
            <linearGradient id="gaugeGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#ef4444" />
              <stop offset="50%" stopColor="#f59e0b" />
              <stop offset="100%" stopColor="#22c55e" />
            </linearGradient>
          </defs>
          <path d="M10,100 A90,90 0 0 1 190,100" fill="none" stroke="url(#gaugeGrad)" strokeWidth={16} strokeLinecap="round" />
          <line x1={100} y1={100} x2={needle.x} y2={needle.y} stroke="#0a1330" strokeWidth={4} strokeLinecap="round" />
          <circle cx={100} cy={100} r={7} fill="#0a1330" />
        </svg>
        <div className="flex justify-between -mt-2 text-[11px] text-ink-muted font-bold px-0.5">
          <span>0</span>
          <span>2,000</span>
        </div>
      </div>
      <div className="text-center mt-1">
        <div className="text-[28px] font-extrabold leading-tight">{totalOrders.toLocaleString()}</div>
        <div className="text-xs text-ink-muted font-semibold">Total Orders</div>
        <div className="text-[11.5px] font-bold text-status-green mt-1">Live count from Order History</div>
      </div>
    </div>
  );
}

/** A real Chart.js bar chart of order value grouped by order type — replaces the old "Sales vs
 *  OP" line chart, which plotted a fabricated day-by-day history (this build tracks no such time
 *  series) alongside an "OP"/gross-margin figure with no cost-basis data model at all. Order
 *  type × value is something this build genuinely has, so the whole card is real now. */
function SalesByType({ data }: { data: { orderType: string; total: number }[] }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);
  const totalSales = useMemo(() => data.reduce((s, d) => s + d.total, 0), [data]);

  useEffect(() => {
    if (!canvasRef.current || data.length === 0) return;
    chartRef.current?.destroy();
    chartRef.current = new Chart(canvasRef.current, {
      type: "bar",
      data: {
        labels: data.map((d) => d.orderType),
        datasets: [{ data: data.map((d) => d.total), backgroundColor: "#2f6fed", borderRadius: 4, maxBarThickness: 46 }],
      },
      options: {
        plugins: { legend: { display: false } },
        scales: {
          y: { ticks: { callback: (v) => `$${(Number(v) / 1000).toFixed(1)}K` }, grid: { color: "#f1f3f8" } },
          x: { grid: { display: false }, ticks: { font: { size: 10.5 } } },
        },
      },
    });
    return () => chartRef.current?.destroy();
  }, [data]);

  return (
    <div className="card p-4 flex flex-col">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div className="text-[11.5px] font-extrabold tracking-wider uppercase text-ink-muted">Sales by Order Type</div>
        <div className="text-[11.5px] font-bold text-status-green">Live · {money(totalSales)} total</div>
      </div>
      {data.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-sm text-ink-muted py-14">No orders recorded yet.</div>
      ) : (
        <canvas ref={canvasRef} height={170} className="mt-2.5" />
      )}
    </div>
  );
}

/** A real, labeled horizontal-bar breakdown — used everywhere a Role Metrics card needs to chart
 *  actual categorized counts (repair status, part status, technician workload, etc.) instead of a
 *  decorative sparkline with no real meaning behind its shape. */
function BreakdownBars({ items, emptyMessage }: { items: { label: string; value: number; color: string }[]; emptyMessage: string }) {
  const max = Math.max(1, ...items.map((i) => i.value));
  if (items.length === 0 || items.every((i) => i.value === 0)) {
    return <div className="text-[11px] text-ink-muted text-center py-2">{emptyMessage}</div>;
  }
  return (
    <div className="flex flex-col gap-1.5">
      {items.map((item) => (
        <div key={item.label} className="flex items-center gap-2 text-[11px]">
          <span className="w-[84px] flex-shrink-0 text-ink-muted font-semibold truncate" title={item.label}>
            {item.label}
          </span>
          <div className="flex-1 h-2.5 rounded-full bg-surface overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${(item.value / max) * 100}%`, backgroundColor: item.color }} />
          </div>
          <span className="w-6 flex-shrink-0 text-right font-bold">{item.value}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * A schematic map of the four Census Bureau US regions (West / Midwest / South / Northeast),
 * correctly oriented and positioned relative to one another — not a precise state-boundary map
 * (no mapping/geo library is used anywhere in this build). This is the one card on this screen
 * that stays illustrative: no region field is tracked on any customer/order record, so there is
 * no real breakdown to chart here at all.
 */
function RegionMap() {
  return (
    <div className="flex items-center gap-2.5">
      <svg viewBox="0 0 200 130" className="w-24 h-[62px] flex-shrink-0">
        <path d="M18,18 L64,26 L60,72 L68,108 L30,116 L16,78 Z" fill="#fbbf24" />
        <path d="M64,26 L112,14 L134,20 L132,56 L100,76 L60,72 Z" fill="#22c55e" />
        <text x="97" y="46" textAnchor="middle" fontSize="11" fontWeight="800" fill="#0a1330">
          Midwest
        </text>
        <path d="M60,72 L100,76 L132,56 L150,82 L168,118 L136,124 L96,120 L68,108 Z" fill="#f59e0b" opacity={0.85} />
        <path d="M132,56 L134,20 L160,26 L176,48 L166,72 L150,82 Z" fill="#fbbf24" opacity={0.9} />
      </svg>
      <div className="flex flex-col gap-1 text-[10.5px] font-bold text-ink-muted">
        <span>
          <span className="inline-block w-2 h-2 rounded-sm bg-[#22c55e] mr-1.5" />
          Midwest (top region)
        </span>
        <span>
          <span className="inline-block w-2 h-2 rounded-sm bg-[#f59e0b] opacity-85 mr-1.5" />
          South
        </span>
        <span>
          <span className="inline-block w-2 h-2 rounded-sm bg-[#fbbf24] mr-1.5" />
          West / Northeast
        </span>
      </div>
    </div>
  );
}

/**
 * A decorative trend line for the one card on this screen that is deliberately kept illustrative
 * (Authorized Performance — there is no authorized-network program modeled anywhere in this
 * build's schema, so there is no real breakdown to chart). Used nowhere else: every other card's
 * chart is the real `BreakdownBars`/`SalesByType`/`RegionMap` above.
 */
function IllustrativeTrendLine({ color }: { color: string }) {
  const values = [58, 64, 60, 70, 66, 74, 71, 78];
  const w = 100;
  const h = 32;
  const pad = 2;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const pts = values
    .map((v, i) => {
      const x = pad + (i / (values.length - 1)) * (w - pad * 2);
      const y = h - pad - ((v - min) / range) * (h - pad * 2);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full h-8 block">
      <polyline points={pts} fill="none" stroke={color} strokeWidth={2.25} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

interface RoleCard {
  key: string;
  title: string;
  value: React.ReactNode;
  trend: React.ReactNode;
  trendUp?: boolean;
  icon: LucideIcon;
  color: "blue" | "green" | "purple" | "orange" | "red" | "indigo";
  chart: "breakdown" | "map" | "illustrative-line";
  breakdown?: { label: string; value: number; color: string }[];
  breakdownEmptyMessage?: string;
  route: string;
}

const ICON_COLOR_HEX: Record<RoleCard["color"], string> = { blue: "#2f6fed", green: "#12924f", purple: "#7c3aed", orange: "#b45309", red: "#d92d20", indigo: "#4f46e5" };
const ICON_BG_CLASS: Record<RoleCard["color"], string> = {
  blue: "bg-status-blue-bg text-brand-blue-dark",
  green: "bg-status-green-bg text-status-green",
  purple: "bg-status-purple-bg text-status-purple",
  orange: "bg-status-orange-bg text-status-orange",
  red: "bg-status-red-bg text-status-red",
  indigo: "bg-status-indigo-bg text-status-indigo",
};

function RoleMetricCard({ card }: { card: RoleCard }) {
  const navigate = useNavigate();
  const Icon = card.icon;
  return (
    <button className="card p-3.5 flex flex-col items-stretch text-left h-full" onClick={() => navigate(card.route)}>
      <div className="flex items-center gap-2.5 mb-3">
        <div className={`w-8 h-8 rounded-[10px] flex items-center justify-center flex-shrink-0 ${ICON_BG_CLASS[card.color]}`}>
          <Icon size={15} />
        </div>
        <div className="text-[11.5px] font-extrabold uppercase tracking-wide leading-tight">{card.title}</div>
      </div>
      <div className="text-[19px] font-extrabold leading-tight mb-0.5">{card.value}</div>
      <div className={`text-[11.5px] font-bold mt-1.5 mb-2.5 ${card.trendUp ? "text-status-green" : "text-ink-muted"}`}>{card.trend}</div>
      <div className="mt-auto pt-1">
        {card.chart === "map" ? (
          <RegionMap />
        ) : card.chart === "illustrative-line" ? (
          <IllustrativeTrendLine color={ICON_COLOR_HEX[card.color]} />
        ) : (
          <BreakdownBars items={card.breakdown ?? []} emptyMessage={card.breakdownEmptyMessage ?? "No data yet."} />
        )}
      </div>
    </button>
  );
}

export function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showingTech, setShowingTech] = useState(user?.role === "TECHNICIAN");

  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [techMetrics, setTechMetrics] = useState<TechnicianMetrics | null>(null);
  const [queue, setQueue] = useState<WorkQueueRow[]>([]);
  const [watchParts, setWatchParts] = useState<{ partNumber: string; description: string; onHand: number; allocated: number; available: number; status: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [briefing, setBriefing] = useState<string | null>(null);
  const [briefingBusy, setBriefingBusy] = useState(false);
  const [briefingError, setBriefingError] = useState<string | null>(null);

  const dateRangeLabel = useMemo(() => {
    const start = new Date(Date.now() - 6 * 86400000);
    const end = new Date();
    const fmt = (d: Date) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    return `${fmt(start)} – ${fmt(end)}, ${end.getFullYear()}`;
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const technicianParam = showingTech && user ? `?technicianName=${encodeURIComponent(user.name)}` : "";
        const [metricsRes, techRes, queueRes] = await Promise.all([
          api.get<DashboardMetrics>("/dashboard/metrics"),
          api.get<TechnicianMetrics>(`/dashboard/technician-metrics${technicianParam}`),
          api.get<WorkQueueRow[]>(`/dashboard/work-queue${technicianParam}`),
        ]);
        if (cancelled) return;
        setMetrics(metricsRes);
        setTechMetrics(techRes);
        setQueue(queueRes);

        if (showingTech) {
          const parts = await api.get<typeof watchParts>("/parts?query=");
          if (!cancelled) setWatchParts(parts.filter((p) => p.status !== "In Stock").slice(0, 5));
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiClientError ? err.message : "Unable to load dashboard data.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showingTech, user]);

  async function generateManagerBriefing() {
    setBriefingBusy(true);
    setBriefingError(null);
    try {
      const { briefing } = await api.post<{ briefing: string }>("/dashboard/manager-briefing");
      setBriefing(briefing);
    } catch (err) {
      setBriefingError(err instanceof ApiClientError ? err.message : "Unable to generate a briefing right now.");
    } finally {
      setBriefingBusy(false);
    }
  }

  const columns: Column<WorkQueueRow>[] = [
    { key: "id", header: "Order #", render: (r) => <span className="font-bold text-brand-blue">{r.id}</span> },
    { key: "customer", header: "Customer", render: (r) => r.customerName },
    { key: "equipment", header: "Equipment", render: (r) => r.equipment },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { key: "promised", header: "Promised Date", render: (r) => r.promisedDate },
  ];

  const roleCards: RoleCard[] = metrics
    ? [
        {
          key: "customer",
          title: "Customer Accounts",
          value: `${metrics.totalCustomers.toLocaleString()} Customers`,
          trend: "Live from Customer records",
          trendUp: true,
          icon: Smile,
          color: "blue",
          chart: "breakdown",
          breakdown: [
            { label: "VIP", value: metrics.vipCustomers, color: ICON_COLOR_HEX.blue },
            { label: "Standard", value: Math.max(0, metrics.totalCustomers - metrics.vipCustomers), color: "#94a3b8" },
          ],
          route: "/orders",
        },
        {
          key: "repair",
          title: "Repair & Service Operations",
          value: `${metrics.repairsCompleted.toLocaleString()} Repairs Completed`,
          trend: "Live from Repair Orders",
          trendUp: true,
          icon: Wrench,
          color: "blue",
          chart: "breakdown",
          breakdown: [
            { label: "Open", value: metrics.repairsOpen, color: "#94a3b8" },
            { label: "In Progress", value: metrics.repairsInProgress, color: "#f59e0b" },
            { label: "Complete", value: metrics.repairsCompleted, color: "#22c55e" },
          ],
          route: "/repair-orders/new",
        },
        {
          key: "productivity",
          title: "Productivity & Workforce Analytics",
          value: `${metrics.technicianWorkload.length} Active Technician${metrics.technicianWorkload.length === 1 ? "" : "s"}`,
          trend: "Live · repair orders per technician",
          trendUp: true,
          icon: Activity,
          color: "purple",
          chart: "breakdown",
          breakdown: metrics.technicianWorkload.slice(0, 4).map((t) => ({ label: t.technician, value: t.count, color: ICON_COLOR_HEX.purple })),
          breakdownEmptyMessage: "No technician assignments yet.",
          route: "/repair-orders/new",
        },
        {
          key: "inventory",
          title: "Inventory & Parts Management",
          value: `${metrics.fillRatePct.toFixed(1)}% Fill Rate`,
          trend: `Live · Inventory Value ${money(metrics.inventoryValue)}`,
          trendUp: true,
          icon: Boxes,
          color: "green",
          chart: "breakdown",
          breakdown: [
            { label: "In Stock", value: metrics.partsInStock, color: "#22c55e" },
            { label: "Low Stock", value: metrics.partsLowStock, color: "#f59e0b" },
            { label: "Out of Stock", value: metrics.partsOutOfStock, color: "#d92d20" },
          ],
          route: "/inventory",
        },
        {
          key: "shipping",
          title: "Shipping Metrics",
          value: `${metrics.ordersShipped.toLocaleString()} Shipped`,
          trend: "Live from Shipping queue",
          trendUp: true,
          icon: Truck,
          color: "blue",
          chart: "breakdown",
          breakdown: [
            { label: "Ready to Ship", value: metrics.ordersReadyToShip, color: "#f59e0b" },
            { label: "In Picking", value: metrics.ordersInPicking, color: "#7c3aed" },
            { label: "Shipped", value: metrics.ordersShipped, color: "#22c55e" },
          ],
          breakdownEmptyMessage: "Nothing in the shipping queue yet.",
          route: "/shipping",
        },
        {
          key: "receiving",
          title: "Receiving Metrics",
          value: `${metrics.cartonsReceived.toLocaleString()} Received`,
          trend: "Live from Receiving cartons",
          trendUp: true,
          icon: PackageSearch,
          color: "orange",
          chart: "breakdown",
          breakdown: [
            { label: "Not Started", value: metrics.cartonsNotStarted, color: "#94a3b8" },
            { label: "In Progress", value: metrics.cartonsInProgress, color: "#f59e0b" },
            { label: "Received", value: metrics.cartonsReceived, color: "#22c55e" },
          ],
          breakdownEmptyMessage: "No cartons logged yet.",
          route: "/receiving",
        },
        {
          key: "regional",
          title: "Regional Metrics & Heatmaps",
          value: (
            <>
              Top Region: Midwest <Illustrative />
            </>
          ),
          trend: "No region field tracked on customer/order records in this build",
          icon: MapIcon,
          color: "orange",
          chart: "map",
          route: "/reports",
        },
        {
          key: "authorized",
          title: "Authorized Performance",
          value: (
            <>
              92.1% Compliance <Illustrative />
            </>
          ),
          trend: "No authorized-network program tracked in this build",
          icon: ShieldCheck,
          color: "blue",
          chart: "illustrative-line",
          route: "/reports",
        },
      ]
    : [];

  if (loading) return <div className="text-ink-muted text-sm py-10 text-center">Loading dashboard…</div>;

  if (error) {
    return (
      <div className="card p-6 max-w-md mx-auto text-center">
        <div className="font-bold mb-2 text-status-red">Couldn't load the dashboard</div>
        <p className="text-sm text-ink-muted mb-4">{error}</p>
        <button className="btn-primary" onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-[13px] font-semibold text-ink-muted">{dateRangeLabel}</span>
        <div className="flex items-center gap-2.5">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-[13px] font-semibold text-ink-muted">
            {user?.serviceCenterId ? `Service Center: ${user.serviceCenterId}` : "All Locations"}
          </span>
          <button className="btn-outline" onClick={() => setShowingTech((v) => !v)}>
            {showingTech ? <LayoutDashboard size={15} /> : <Wrench size={15} />}
            {showingTech ? "View Manager Dashboard" : "View Technician Dashboard"}
          </button>
        </div>
      </div>

      {!showingTech && metrics && (
        <>
          <div className="card p-4 mb-4">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
              <div className="text-[11.5px] font-extrabold tracking-wider uppercase text-ink-muted flex items-center gap-2">
                <Sparkles size={14} /> Daily Manager Briefing (AI)
              </div>
              <button className="btn-outline" disabled={briefingBusy} onClick={() => void generateManagerBriefing()}>
                <Sparkles size={14} /> {briefingBusy ? "Generating…" : "Generate Briefing"}
              </button>
            </div>
            <p className="text-xs text-ink-muted mb-2">Combines parts with real repair/order usage in the last 7 days (ranked by usage-vs-availability pressure — not a full inventory scan), technician workload/overdue, and placeholder-pricing signals into one AI-narrated summary of what needs your attention today.</p>
            {briefingError && <p className="text-xs font-semibold text-status-red">{briefingError}</p>}
            {briefing && <p className="text-sm bg-surface-muted rounded-lg p-3.5 mt-1 whitespace-pre-line">{briefing}</p>}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4 items-stretch">
            <Orderometer totalOrders={metrics.totalOrders} />
            <SalesByType data={metrics.ordersByType} />
            <div className="card p-4 flex flex-col">
              <div className="text-[11.5px] font-extrabold tracking-wider uppercase text-ink-muted mb-2">At a Glance</div>
              <div className="flex-1 flex flex-col justify-between gap-2">
                {[
                  { icon: Receipt, color: "blue" as const, value: metrics.totalOrders.toLocaleString(), label: "Total Orders", trend: "Live count from Order History" },
                  { icon: DollarSign, color: "green" as const, value: money(metrics.totalSales), label: "Total Sales", trend: "Live from Order History" },
                  { icon: Wrench, color: "purple" as const, value: metrics.repairsCompleted.toLocaleString(), label: "Repairs Completed", trend: "Live from Repair Orders" },
                  { icon: Boxes, color: "orange" as const, value: `${metrics.fillRatePct.toFixed(1)}%`, label: "Parts Fill Rate", trend: "Live from Parts Inventory" },
                ].map((g) => (
                  <div key={g.label} className="flex items-center gap-3 py-1">
                    <div className={`w-10 h-10 rounded-[10px] flex items-center justify-center flex-shrink-0 ${ICON_BG_CLASS[g.color]}`}>
                      <g.icon size={18} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-[11.5px] text-ink-muted font-semibold truncate">{g.label}</span>
                        <span className="text-lg font-extrabold whitespace-nowrap">{g.value}</span>
                      </div>
                      <div className="text-[11px] font-bold text-status-green">{g.trend}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="text-[11.5px] font-extrabold tracking-wider uppercase text-ink-muted mb-3">Role Metrics</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5 mb-3">
            {roleCards.map((c) => (
              <RoleMetricCard key={c.key} card={c} />
            ))}
          </div>

          <div className="text-xs text-ink-muted text-center mt-5">All figures are live as of {new Date().toLocaleString("en-US")}</div>
        </>
      )}

      {showingTech && techMetrics && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3.5 mb-4.5">
            {[
              { icon: FolderOpen, color: "blue" as const, label: "My Open Orders", value: techMetrics.openOrders, trend: "Live from the work queue" },
              { icon: Clock, color: "orange" as const, label: "In Progress", value: techMetrics.inProgress, trend: "Live from the work queue" },
              { icon: CheckCircle2, color: "green" as const, label: "Completed Today", value: techMetrics.completedToday, trend: "Inspected/closed today" },
              { icon: CalendarCheck, color: "purple" as const, label: "Due Today", value: techMetrics.dueToday, trend: "Promised date is today" },
              { icon: AlertTriangle, color: "red" as const, label: "Overdue", value: techMetrics.overdue, trend: techMetrics.overdue > 0 ? "Past promised date" : "Nothing overdue", trendUp: techMetrics.overdue === 0 },
            ].map((k) => (
              <div key={k.label} className="card p-3.5">
                <div className="flex items-center gap-2.5 mb-2.5">
                  <div className={`w-8 h-8 rounded-[10px] flex items-center justify-center flex-shrink-0 ${ICON_BG_CLASS[k.color]}`}>
                    <k.icon size={16} />
                  </div>
                  <div className="text-xs font-semibold text-ink-muted">{k.label}</div>
                </div>
                <div className="text-xl font-extrabold">{k.value}</div>
                <div className={`text-[11.5px] font-bold mt-1.5 ${k.trendUp || k.label === "Completed Today" ? "text-status-green" : k.label === "Overdue" && k.value > 0 ? "text-status-red" : "text-ink-muted"}`}>{k.trend}</div>
              </div>
            ))}
            <div className="card p-3.5">
              <div className="flex items-center gap-2.5 mb-2.5">
                <div className="w-8 h-8 rounded-[10px] flex items-center justify-center flex-shrink-0 bg-status-indigo-bg text-status-indigo">
                  <BarChart3 size={16} />
                </div>
                <div className="text-xs font-semibold text-ink-muted">
                  My Efficiency <Illustrative />
                </div>
              </div>
              <div className="text-xl font-extrabold">92%</div>
              <div className="text-[11.5px] font-bold mt-1.5 text-ink-muted">No labor-efficiency data tracked in this build</div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4.5 items-start">
            <div className="card overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3.5 border-b border-border">
                <div className="font-bold text-sm">My Work Queue</div>
                <button className="text-xs font-bold text-brand-blue" onClick={() => navigate("/orders")}>
                  View all ›
                </button>
              </div>
              <DataTable columns={columns} rows={queue} rowKey={(r) => r.id} emptyMessage="No open repair orders." />
            </div>
            <div className="card p-4">
              <div className="font-bold text-sm mb-3">My Recent Repair Activity</div>
              {queue.length === 0 ? (
                <div className="text-sm text-ink-muted text-center py-6">Nothing assigned yet.</div>
              ) : (
                <div className="flex flex-col">
                  {queue.slice(0, 5).map((r) => (
                    <button key={r.id} className="flex items-center gap-2.5 py-2.5 border-b border-border last:border-0 text-left hover:bg-surface -mx-1 px-1" onClick={() => navigate(`/repair-orders/${r.id}`)}>
                      <div className="w-[30px] h-[30px] rounded-[10px] flex items-center justify-center flex-shrink-0 bg-status-blue-bg text-brand-blue-dark">
                        <Wrench size={15} />
                      </div>
                      <div className="min-w-0">
                        <div className="text-[13px] truncate">
                          {r.id} — {r.customerName} — {r.status}
                        </div>
                        <div className="text-xs text-ink-muted">Promised {r.promisedDate}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="card p-4 mb-4.5">
            <div className="font-bold text-sm mb-1">
              My Performance (MTD) <Illustrative />
            </div>
            <p className="text-xs text-ink-muted mb-3.5">No monthly performance-tracking data model exists in this build yet — figures below are illustrative only.</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: "Orders Completed", value: "42", trend: "▲ 8 vs last month" },
                { label: "Avg. Turnaround Time", value: "1.6 days", trend: "▲ 0.3" },
                { label: "First Time Fix Rate", value: "86%", trend: "▲ 4%" },
                { label: "Customer Rating", value: "4.7 ★", trend: "▲ 0.2" },
              ].map((p) => (
                <div key={p.label}>
                  <div className="text-xs text-ink-muted font-semibold">{p.label}</div>
                  <div className="text-xl font-extrabold">{p.value}</div>
                  <div className="text-[11.5px] font-bold text-status-green mt-0.5">{p.trend}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="card overflow-hidden mb-4.5">
            <div className="flex items-center justify-between px-4 py-3.5 border-b border-border">
              <div className="font-bold text-sm">Inventory to Watch (Low Stock / High Usage)</div>
              <button className="text-xs font-bold text-brand-blue" onClick={() => navigate("/inventory")}>
                View all ›
              </button>
            </div>
            <DataTable
              columns={[
                { key: "part", header: "Part Number", render: (p) => <span className="font-mono text-xs">{p.partNumber}</span> },
                { key: "desc", header: "Description", render: (p) => p.description },
                { key: "onhand", header: "On Hand", render: (p) => p.onHand },
                { key: "alloc", header: "Allocated", render: (p) => p.allocated },
                { key: "avail", header: "Available", render: (p) => p.available },
              ]}
              rows={watchParts}
              rowKey={(p) => p.partNumber}
              onRowClick={() => navigate("/inventory")}
              emptyMessage="Nothing low on stock right now."
            />
          </div>

          <div className="card p-4">
            <div className="font-bold text-sm mb-3.5">Quick Actions</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: "Create Repair Order", icon: ClipboardPlus, to: "/repair-orders/new" },
                { label: "Lookup Order", icon: Search, to: "/orders" },
                { label: "Order Parts", icon: ShoppingCart, to: "/inventory" },
                { label: "Print Labels", icon: Printer, to: "/receiving" },
              ].map((a) => (
                <button key={a.label} className="btn-outline flex-col h-20 justify-center" onClick={() => navigate(a.to)}>
                  <a.icon size={20} />
                  <span className="text-xs mt-1.5">{a.label}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
